/* ═══════════════════════════════════════════════════════════════════════════
 * L'IDENTITÉ DE CE QUI EST ANALYSÉ
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ POURQUOI CETTE FONCTION EXISTE, ET POURQUOI ELLE EST À PART.
 *    `IAAnalysisPanel` doit jeter son résultat dès que l'objet analysé change.
 *    Mais ses appelants passent `data` en OBJET LITTÉRAL inline
 *    (`data={{ exercice: annee }}`) : son identité JavaScript change à CHAQUE
 *    rendu du parent. Une dépendance d'effet sur `data` remettrait donc le
 *    panneau à zéro en permanence, y compris quand rien n'a bougé.
 *    ➡️ On compare par VALEUR, pas par référence. C'est cette fonction.
 *
 * ⚠️ ELLE EST DANS SON PROPRE FICHIER POUR ÊTRE ÉPROUVÉE. Le dépôt n'a ni
 *    `@testing-library` ni `setupTests` : aucun composant ne peut être monté en
 *    test. Sortir la RÈGLE du composant la rend vérifiable sans rien installer —
 *    même choix que `montant.js` et `composants-offre.js`, qui ont leurs tests.
 *
 * ⓘ L'ORDRE DES CLÉS EST STABLE : les appelants construisent le même littéral à
 *   chaque rendu, donc `JSON.stringify` rend la même chaîne. On ne trie pas les
 *   clés — un tri donnerait l'illusion de gérer un cas (deux appelants avec des
 *   ordres différents) qui ne se produit pas, puisqu'un composant n'a qu'un
 *   appelant à la fois.
 *
 * ⓘ COÛT : la plus grosse charge mesurée est la balance comptable — 43 comptes
 *   de 7 champs (plan comptable en production, 13/09/2026). Sérialiser cela à
 *   chaque rendu est négligeable devant le rendu lui-même.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Rend une clé stable décrivant CE QUI est analysé.
 * Deux appels successifs avec les mêmes valeurs rendent la même clé, quelles
 * que soient les identités d'objet.
 *
 * @param {string} type  famille d'analyse (`fec`, `balance`, `bulletin`...)
 * @param {object} data  charge envoyée à `/ia/analyze/:type`
 * @returns {string}
 */
export function identiteAnalyse(type, data) {
  try {
    return JSON.stringify({ type: type ?? null, data: data ?? null });
  } catch (_) {
    // ⚠️ UNE CHARGE NON SÉRIALISABLE (cycle, BigInt) NE DOIT PAS FAIRE CROIRE
    //    QUE RIEN N'A CHANGÉ. On rend une clé UNIQUE : le panneau se remettra à
    //    zéro à chaque rendu — bruyant, visible, corrigeable. L'inverse serait
    //    un résultat périmé affiché en silence, ce que ce fichier existe pour
    //    empêcher.
    return `${type ?? ''}::non-serialisable::${Date.now()}::${Math.random()}`;
  }
}

export default identiteAnalyse;
